import React, { useEffect, useState } from 'react';
import { PageHeader, PageLoader, EmptyState } from '@/components/common/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

export default function OrganizationsAdminPage() {
  const [orgs, setOrgs] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get('/organizations').then(r => setOrgs(r.data));
  useEffect(() => { load(); }, []);

  const suspend = async (o) => { await api.post(`/organizations/${o.id}/suspend`); load(); };
  const activate = async (o) => { await api.post(`/organizations/${o.id}/activate`); load(); };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader title="Organisations" subtitle="Multi-tenant management of customer organisations."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-org-btn"><Plus size={14} className="mr-1" /> New organisation</Button></DialogTrigger>
            <NewOrgDialog onCreated={() => { setOpen(false); load(); }} onCancel={() => setOpen(false)} />
          </Dialog>
        }
      />
      {!orgs ? <PageLoader /> : orgs.length === 0 ? <EmptyState title="No organisations yet" /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orgs.map(o => (
            <Card key={o.id} className="p-4" data-testid={`org-card-${o.name}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold">{o.name}</div>
                  <div className="text-xs text-muted-foreground">{o.type} · {o.sector}</div>
                </div>
                <Badge variant={o.status === 'active' ? 'default' : 'destructive'} className="text-[10px]">{o.status}</Badge>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div className="text-muted-foreground">Users limit</div><div className="font-mono">{o.max_users}</div>
                <div className="text-muted-foreground">Assets limit</div><div className="font-mono">{o.max_assets}</div>
                <div className="text-muted-foreground">Address</div><div className="truncate">{o.address || '—'}</div>
              </div>
              <div className="mt-3 flex gap-2">
                {o.status === 'active'
                  ? <Button size="sm" variant="outline" onClick={() => suspend(o)}>Suspend</Button>
                  : <Button size="sm" onClick={() => activate(o)}>Activate</Button>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function NewOrgDialog({ onCreated, onCancel }) {
  const [f, setF] = useState({ name: '', type: 'PSU', sector: 'Hydropower', address: '', primary_contact: '', max_users: 50, max_assets: 20 });
  const submit = async () => {
    try {
      await api.post('/organizations', f);
      toast.success('Organisation created');
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not create'); }
  };
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>New organisation</DialogTitle></DialogHeader>
      <div className="space-y-3 mt-2">
        <div><Label>Name</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="org-form-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Type</Label><Input value={f.type} onChange={e => setF({ ...f, type: e.target.value })} /></div>
          <div><Label>Sector</Label><Input value={f.sector} onChange={e => setF({ ...f, sector: e.target.value })} /></div>
        </div>
        <div><Label>Address</Label><Input value={f.address} onChange={e => setF({ ...f, address: e.target.value })} /></div>
        <div><Label>Primary contact</Label><Input value={f.primary_contact} onChange={e => setF({ ...f, primary_contact: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Max users</Label><Input type="number" value={f.max_users} onChange={e => setF({ ...f, max_users: parseInt(e.target.value || '0') })} /></div>
          <div><Label>Max assets</Label><Input type="number" value={f.max_assets} onChange={e => setF({ ...f, max_assets: parseInt(e.target.value || '0') })} /></div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} disabled={!f.name} data-testid="org-form-submit">Create</Button>
      </DialogFooter>
    </DialogContent>
  );
}
